import {
  Avatar,
  Box,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  logOutAction,
  selectEmail,
  selectProfileImage,
  selectUserName,
  setLastVisitedLink,
} from "redux/authSlice";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import history from "utils/history";
import CustomPopper from "global/components/CustomPopper/CustomPopper";
import { usePopover } from "hooks/use-popover";
import { RouterLink } from "components/router-link";
import viewpaths from "global/constants/ViewPathConstants";
import { hasAccessTo } from "layouts/Config";
import strings from "global/constants/StringConstants";
import DropdownDownIcon from "assets/icons1/DropdownDownIcon.svg";
import MyProfileIcon from "assets/icons1/TopNavIcons/MyProfileIcon.svg";
import MyPlanIcon from "assets/icons1/TopNavIcons/MyPlanIcon.svg";
import SenderDomains from "assets/icons1/TopNavIcons/SenderDomains.svg";
import RolesIcon from "assets/icons1/TopNavIcons/RolesIcon.svg";
import UsersIcon from "assets/icons1/TopNavIcons/UsersIcon.svg";
import LogoutIcon from "assets/icons1/TopNavIcons/LogoutIcon.svg";
import SecurityIcon from "assets/icons1/TopNavIcons/SecurityIcon.svg";

const TopNavSection = () => {
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));
  const userEmail = useAppSelector(selectEmail);
  const userName = useAppSelector(selectUserName);
  const dispatch = useAppDispatch();
  const popover = usePopover<any>();
  const profileImage = useAppSelector(selectProfileImage);

  const handleLogout = () => {
    const path = history.location.pathname + history.location.search;
    dispatch(logOutAction({}));
    dispatch(setLastVisitedLink(path));
    history.push(viewpaths.landingViewPath);
  };

  const menuItems = [
    {
      href: viewpaths.myProfileViewPath,
      icon: <img src={MyProfileIcon} alt="MyProfileIcon" />,
      label: "My Profile",
      condition: true,
    },
    {
      href: `${viewpaths.viewMyPlanViewPath}?tab=account-usage`,
      icon: <img src={MyPlanIcon} alt="MyPlanIcon" />,
      label: "My Plan",
      condition: hasAccessTo(strings.billing, strings.fetchPermission),
    },
    {
      href: `${viewpaths.domainsViewPath}`,
      icon: <img src={SenderDomains} alt="SenderDomains"/>,
      label: "Senders, Domains & Dedicated IPs",
      condition: hasAccessTo(strings.app, strings.adminPermission),
    },
    {
      href: `${viewpaths.rolesViewPath}`,
      icon: <img src={RolesIcon} alt="RolesIcon" />,
      label: "Roles",
      condition: hasAccessTo(strings.app, strings.adminPermission),
    },
    {
      href: `${viewpaths.usersViewPath}`,
      icon: <img src={UsersIcon} alt="UsersIcon" />,
      label: "Users",
      condition: hasAccessTo(strings.app, strings.adminPermission),
    },
    {
      href: viewpaths.securityViewPath,
      icon: <img src={SecurityIcon} alt="SecurityIcon" />,
      label: "Security",
      condition: true,
    },
  ];

  const getMenuSection = () => {
    return (
      <Stack direction="column" spacing={0}>
        {!lgUp && (
          <>
            <Stack sx={{ textAlign: "start" }} p={1}>
              <Typography variant="body1" px={1.2}>
                {userName}{" "}
              </Typography>
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{ fontSize: "0.75rem" }}
                px={1.2}
              >
                {userEmail}
              </Typography>
            </Stack>
            <Divider />
          </>
        )}
        <Box sx={{ p: 1 }}>
          {menuItems.map(
            (item, index) =>
              item.condition && (
                <>
                  <ListItemButton
                    key={index}
                    href={item.href}
                    onClick={popover.handleClose}
                    sx={{
                      borderRadius: 1,
                      px: 1,
                      py: 1,
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                    LinkComponent={RouterLink || "div"}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight={500}>
                          {item.label}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                  <Divider
                    sx={{
                      borderColor: "rgba(71, 85, 105, 0.25)",
                    }}
                  />
                </>
              )
          )}
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 1,
              px: 1,
              pt: 1,
              pb: 0,
            }}
            LinkComponent={"button"}
          >
            <ListItemIcon>
              <img src={LogoutIcon} alt="LogoutIcon" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="subtitle2" fontWeight={500}>
                  Logout
                </Typography>
              }
            />
          </ListItemButton>
        </Box>
      </Stack>
    );
  };

  return (
    <Stack direction="row">
      {lgUp && (
        <Stack sx={{ textAlign: "right" }} pr={1}>
          <Typography variant="body1">{userName}</Typography>
          <Typography
            color="text.secondary"
            variant="body2"
            sx={{ fontSize: "0.75rem" }}
          >
            {userEmail}
          </Typography>
        </Stack>
      )}
      <Box
        onClick={popover.handleOpen}
        ref={popover.anchorRef}
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          borderWidth: 2,
          borderStyle: "solid",
          borderColor: "divider",
          height: 40,
          width: 40,
          borderRadius: "50%",
          cursor: "pointer",
        }}
      >
        <Avatar
          sx={{
            height: 32,
            width: 32,
          }}
          src={profileImage}
        />
      </Box>
      <img
        src={DropdownDownIcon}
        ref={popover.anchorRef}
        onClick={popover.handleOpen}
        style={{
          cursor: "pointer",
        }}
        alt=""
      />
      <CustomPopper
        open={popover.open}
        anchorRef={popover.anchorRef.current}
        handleClose={popover.handleClose}
        children={getMenuSection()}
        width={lgUp ? hasAccessTo(strings.app, strings.adminPermission) ? "20rem" : "9rem" : "auto"}
      />
    </Stack>
  );
};

export default TopNavSection;
